import React, { useState, useEffect, useRef, useCallback } from 'react';
import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';

import {
  CharacterFeatureSource,
  CharacterSheetInterface,
  BasicCreatureInstance,
  SummonInstance,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import ReusableStringBox from "@dndComponents/ReusableStringBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import ReplacerSelectorModal from "@dndComponents/ReplacerSelectorModal";
import DandDButtonBox from './DandDButtonBoxSimple';

interface Props {
  name: string;
  value: string;
  cfs?: CharacterSheetInterface | BasicCreatureInstance | SummonInstance | CharacterFeatureSource;
  onChange: (value: string) => void;
  disabled?: boolean;
}

interface Selection {
  start: number;
  end: number;
}

const common_util = CommonUtilities.getInstance();

function toggleTag(text: string, sel: Selection, tag: string): { next: string; sel: Selection } {
  const { start, end } = sel;
  const open = `<${tag}>`;
  const close = `</${tag}>`;
  const selected = text.substring(start, end);

  if (selected.startsWith(open) && selected.endsWith(close)) {
    const inner = selected.slice(open.length, selected.length - close.length);
    const next = text.substring(0, start) + inner + text.substring(end);
    return { next, sel: { start, end: start + inner.length } };
  }

  const before = text.substring(0, start);
  const after = text.substring(end);
  if (before.endsWith(open) && after.startsWith(close)) {
    const next = before.slice(0, before.length - open.length) + selected + after.slice(close.length);
    return { next, sel: { start: start - open.length, end: end - open.length } };
  }

  const wrapped = open + selected + close;
  const next = text.substring(0, start) + wrapped + text.substring(end);
  return { next, sel: { start, end: start + wrapped.length } };
}

export default function StringBoxWithReusables({
  name,
  value,
  cfs,
  onChange,
  disabled = false,
}: Props) {
  const [my_value, setValue] = useState(value);
  const [prev_value, setPrevValue] = useState(value);
  const [view_plain, setViewPlain] = useState(false);
  const [change_time, setChangeTime] = useState(0);
  const [replacer_modal_open, setReplacerModalOpen] = useState(false);
  const pending_selection = useRef<Selection | null>(null);
  const selection = useRef<Selection>({ start: 0, end: 0 });
  const textarea_ref = useRef<HTMLTextAreaElement | null>(null);
  const my_value_ref = useRef(my_value);

  useEffect(() => {
    // Only sync from props if this is a genuine external change —
    // i.e. the incoming value differs from what we last sent out via onChange.
    // This prevents the prop bouncing back from the parent from overwriting
    // in-progress edits.
    if (value !== prev_value) {
      setPrevValue(value);
      // Only reset local state if the parent is pushing a value we didn't originate.
      // my_value holds what we last committed, so if value !== my_value it's truly external.
      if (value !== my_value) {
        my_value_ref.current = value;
        setValue(value);
      }
    }
  }, [value, prev_value, my_value]);

  const handleCursorCapture = (e: React.SyntheticEvent<HTMLDivElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (target.selectionStart != null && target.selectionEnd != null) {
      selection.current = { start: target.selectionStart, end: target.selectionEnd };
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        textarea_ref.current = target as HTMLTextAreaElement;
      }
    }
  };

  // Restore selection after the new value has rendered into the textarea.
  useEffect(() => {
    if (pending_selection.current && textarea_ref.current) {
      const { start, end } = pending_selection.current;
      pending_selection.current = null;
      textarea_ref.current.focus();
      textarea_ref.current.setSelectionRange(start, end);
    }
  }, [change_time]);

  // Called on every keystroke from StringBox — keeps my_value_ref current
  // without triggering a full save/re-render cycle.
  const handleLiveChange = useCallback((changed: string) => {
    my_value_ref.current = changed;
    setValue(changed);
  }, []);

  const commit = useCallback((next: string, nextSel?: Selection) => {
    my_value_ref.current = next;
    setValue(next);
    onChange(next);
    setChangeTime(common_util.get_time_number());
    if (nextSel) {
      selection.current = nextSel;
      pending_selection.current = nextSel;
    }
  }, [onChange]);

  const applyTag = useCallback((tag: string) => {
    if (textarea_ref.current) {
      const el = textarea_ref.current;
      selection.current = {
        start: el.selectionStart ?? selection.current.start,
        end: el.selectionEnd ?? selection.current.end,
      };
    }
    const { next, sel } = toggleTag(my_value_ref.current, selection.current, tag);
    commit(next, sel);
  }, [commit]);

  const insertAtCursor = useCallback((insert: string) => {
    if (textarea_ref.current) {
      const el = textarea_ref.current;
      selection.current = {
        start: el.selectionStart ?? selection.current.start,
        end: el.selectionEnd ?? selection.current.end,
      };
    }
    const { start, end } = selection.current;
    const next = my_value_ref.current.substring(0, start) + insert + my_value_ref.current.substring(end);
    const newPos = start + insert.length;
    commit(next, { start: newPos, end: newPos });
  }, [commit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<Element>) => {
    if (!e.ctrlKey && !e.metaKey) return;
    const tagMap: Record<string, string> = { b: 'b', i: 'i', u: 'u' };
    const key = e.key.toLowerCase();
    if (tagMap[key]) {
      e.preventDefault();
      applyTag(tagMap[key]);
    }
  }, [applyTag]);

  // Prevents toolbar buttons from stealing focus/selection from the textarea.
  const noFocusSteal = (e: React.MouseEvent) => e.preventDefault();

  const handleAutoTag = () => {
    let changed = "";
    for (const line of my_value.split("\n")) {
      let line2 = line;
      if (line2.startsWith("Using a Higher-Level Spell Slot.")) {
        line2 = line2.replace("Using a Higher-Level Spell Slot.", "<b>Using a Higher-Level Spell Slot.</b>");
      } else if (line2.startsWith("Using a Higher-Level Spell Slot:")) {
        line2 = line2.replace("Using a Higher-Level Spell Slot:", "<b>Using a Higher-Level Spell Slot:</b>");
      } else {
        let index = line2.indexOf(".");
        const index2 = line2.indexOf(":");
        if (index2 > 0 && (index === -1 || index > index2)) index = index2;
        if (index > 0) {
          const sub = line2.substring(0, index + 1);
          const words = sub.split(" ");
          if (words.length < 5 && !words.some(o => o.includes("("))) {
            line2 = line2.replace(sub, `<b>${sub}</b>`);
          }
        }
      }
      changed += line2 + "\n";
    }
    while (changed.includes("<b><b>")) changed = common_util.replaceAll(changed, "<b><b>", "<b>");
    while (changed.includes("</b></b>")) changed = common_util.replaceAll(changed, "</b></b>", "</b>");
    commit(changed.trim());
  };

  const parseMarkdownTable = (md: string): string | null => {
    const lines = md.trim().split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return null;
    if (!lines.every(l => l.startsWith("|") && l.endsWith("|"))) return null;
    const isSeparator = (l: string) => /^\|[\s|:-]+\|$/.test(l);
    if (!isSeparator(lines[1])) return null;
    const splitRow = (l: string) => l.slice(1, -1).split("|").map(c => c.trim());
    const headers = splitRow(lines[0]);
    const dataRows = lines.slice(2).map(splitRow);
    let html = "<table>\n";
    html += "<tr>" + headers.map(h => `<th>${h}</th>`).join("") + "</tr>\n";
    for (const row of dataRows) {
      html += "<tr>" + row.map(c => `<td>${c}</td>`).join("") + "</tr>\n";
    }
    html += "</table>";
    return html;
  };

  const iconBtnSx = {
    padding: '4px',
    color: 'rgba(255,255,255,0.7)',
    '&:hover': { color: '#fff' },
  };

  const renderToolbar = () => (
    <>
      <Grid size="auto">
        <Tooltip title="Bold (Ctrl+B)">
          <span>
            <IconButton size="small" sx={iconBtnSx} onMouseDown={noFocusSteal} onClick={() => applyTag('b')}>
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid size="auto">
        <Tooltip title="Italic (Ctrl+I)">
          <span>
            <IconButton size="small" sx={iconBtnSx} onMouseDown={noFocusSteal} onClick={() => applyTag('i')}>
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid size="auto">
        <Tooltip title="Underline (Ctrl+U)">
          <span>
            <IconButton size="small" sx={iconBtnSx} onMouseDown={noFocusSteal} onClick={() => applyTag('u')}>
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>

      <Grid size="auto">
        <Tooltip title="Insert Table">
          <span>
            <IconButton
              size="small"
              sx={iconBtnSx}
              onMouseDown={noFocusSteal}
              onClick={() => {
                const { start, end } = selection.current;
                if (start !== end) {
                  const selected = my_value.substring(start, end);
                  const htmlTable = parseMarkdownTable(selected);
                  if (htmlTable) {
                    const next = my_value.substring(0, start) + htmlTable + my_value.substring(end);
                    commit(next, { start, end: start + htmlTable.length });
                    return;
                  }
                }
                insertAtCursor("\n<b></b>\n<table>\n<tr><th></th><th></th></tr>\n<tr><td></td><td></td></tr>\n</table>\n");
              }}
            >
              <TableChartIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid size="auto">
        <Tooltip title="Auto Tag">
          <span>
            <IconButton size="small" sx={iconBtnSx} onMouseDown={noFocusSteal} disabled={disabled} onClick={handleAutoTag}>
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid size="auto">
        <Tooltip title="Insert Replacer">
          <span>
            <IconButton size="small" sx={iconBtnSx} onMouseDown={noFocusSteal} onClick={() => setReplacerModalOpen(true)}>
              <AddCircleIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid size="auto">
        <Tooltip title="Clean Description">
          <span>
            <IconButton
              size="small"
              sx={iconBtnSx}
              onMouseDown={noFocusSteal}
              onClick={() => {
                const { start, end } = selection.current;
                if (start !== end) {
                  const cleaned = common_util.cleanDescription(my_value.substring(start, end));
                  const next = my_value.substring(0, start) + cleaned + my_value.substring(end);
                  commit(next, { start, end: start + cleaned.length });
                } else {
                  commit(common_util.cleanDescription(my_value));
                }
              }}
            >
              <CleaningServicesIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>

      <Grid size={2}>
        <DandDButtonBox
          name="Insert Stat Block"
          onMouseDown={noFocusSteal}
          onClick={() => {
            let str = `<div class="stat-block">\n`;
            str += `<h4 class="compendium-hr">Name</h4>\n`;
            str += `<p>Size CreatureType, Alignment</p>\n`;
            str += `<p><strong>AC</strong> 15</p>\n`;
            str += `<p><strong>HP</strong> 15</p>\n`;
            str += `<p><strong>Speed</strong> 30 ft.</p>\n`;
            str += `<div class="stats">\n`;
            str += `<table class="physical abilities-saves">\n`;
            str += `<thead>\n`;
            str += `<tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>\n`;
            str += `</thead>\n`;
            str += `<tbody>\n`;
            str += `<tr><th>STR</th><td>16</td><td>+3</td><td>+3</td></tr>\n`;
            str += `<tr><th>DEX</th><td>16</td><td>+3</td><td>+3</td></tr>\n`;
            str += `<tr><th>CON</th><td>16</td><td>+3</td><td>+3</td></tr>\n`;
            str += `</tbody>\n`;
            str += `</table>\n`;
            str += `<table class="mental abilities-saves">\n`;
            str += `<thead>\n`;
            str += `<tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>\n`;
            str += `</thead>\n`;
            str += `<tbody>\n`;
            str += `<tr><th>INT</th><td>16</td><td>+3</td><td>+3</td></tr>\n`;
            str += `<tr><th>WIS</th><td>16</td><td>+3</td><td>+3</td></tr>\n`;
            str += `<tr><th>CHA</th><td>16</td><td>+3</td><td>+3</td></tr>\n`;
            str += `</tbody>\n`;
            str += `</table>\n`;
            str += `</div>\n`;
            str += `<p><strong>Immunities</strong> DamageTypes; Conditions</p>\n`;
            str += `<p><strong>Senses</strong> SenseType 30 ft.; Passive Perception 6</p>\n`;
            str += `<p><strong>Languages</strong> Understands the languages you know</p>\n`;
            str += `<p><strong>CR</strong> None (XP 0; PB equals your Proficiency Bonus)</p>\n`;
            str += `<p class="monster-header">Actions</p>\n`;
            str += `<p><strong><em>Slam.</em></strong> <em>Melee Attack Roll:</em> Bonus equals your spell attack modifier, reach 5 ft. <em>Hit:</em> Force damage equal to 1d4 + 3 (Medium or smaller), 2d6 + 3 + your spellcasting ability modifier (Large), or 2d12 + 3 + your spellcasting ability modifier (Huge).</p>\n`;
            str += `</div>\n`;
            insertAtCursor(str);
          }}
        />
      </Grid>
      <Grid size={2}>
        <DandDButtonBox
          name="Remove Attrs"
          onMouseDown={noFocusSteal}
          onClick={() => commit(common_util.removeTagAttributes(my_value))}
        />
      </Grid>
      <Grid size={2}>
        <ToggleButtonBox
          name="View Plain"
          value={view_plain}
          onMouseDown={noFocusSteal}
          onToggle={() => setViewPlain(v => !v)}
        />
      </Grid>
    </>
  );

  return (
    <div style={{ backgroundColor: "black" }}>
      <Grid container spacing={0} direction="row" alignItems="center">
        {disabled ? (
          <Grid size={12}>
            <DisplayStringWithReplacements display_string={my_value} cfs={cfs} />
          </Grid>
        ) : (
          <>
            <Grid size={12}><ReusableStringBox /></Grid>
            {renderToolbar()}
            <Grid
              size={12}
              onSelect={handleCursorCapture}
              onMouseUp={handleCursorCapture}
              onKeyUp={handleCursorCapture}
            >
              {view_plain ? (
                <DisplayStringWithReplacements display_string={my_value} cfs={cfs} />
              ) : (
                <StringBox
                  name={name}
                  value={my_value}
                  multiline
                  color={"white"}
                  change_time={change_time}
                  onKeyDown={handleKeyDown}
                  onChange={(changed: string) => {
                    handleLiveChange(changed);
                  }}
                  onBlur={(changed: string) => {
                    console.log(changed);
                    commit(changed);
                  }}
                  onSelect={handleCursorCapture}
                />
              )}
            </Grid>

            <ReplacerSelectorModal
              open={replacer_modal_open}
              onClose={() => setReplacerModalOpen(false)}
              onInsert={insertAtCursor}
            />
          </>
        )}
      </Grid>
    </div>
  );
}